// WikiBrowse.jsx — department landing: hero, required-reading strip, folder cards.
import React from 'react';
import {
    Folder, FileText, ChevronRight, ShieldCheck, Lock, BellRing, Sparkles, FileClock,
} from 'lucide-react';
import { DEPT_BY_ID, deptColors, relativeTime } from '../../lib/wikiConstants';
import { countDocs } from './WikiTree';

function flattenDocs(nodes, out = []) {
    (nodes || []).forEach(n => n.kind === 'doc' ? out.push(n) : flattenDocs(n.children, out));
    return out;
}

export default function WikiBrowse({ deptId, access, nodes, filter = '', onSelectDoc, onOpenAgent }) {
    const dept = DEPT_BY_ID[deptId];
    const colors = deptColors(deptId);
    const needsRead = flattenDocs(nodes).filter(d => d.read_required && !d.read && !d.locked && d.status !== 'draft');
    const q = (filter || '').trim().toLowerCase();
    const results = q ? flattenDocs(nodes).filter(d => (d.name || '').toLowerCase().includes(q)) : null;

    const DocRow = ({ node }) => {
        const locked = !!node.locked;
        return (
            <div onClick={() => !locked && onSelectDoc(node.page_id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border-b border-stone-200 last:border-b-0
                    ${locked ? 'opacity-60 cursor-default' : 'cursor-pointer hover:bg-stone-50'}`}>
                <FileText className="w-4 h-4 text-stone-500 shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-stone-900 truncate">{node.name}</span>
                        {node.version > 1 && <span className="text-[10px] font-mono text-stone-400">v{node.version}</span>}
                        {node.status === 'draft' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1"><FileClock className="w-2.5 h-2.5" /> Draft</span>}
                        {node.read_required && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-800 border border-stone-300">Required</span>}
                        {locked && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 border border-stone-200 inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Restricted</span>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{node.owner_name} · edited {relativeTime(node.updated_at)}</div>
                </div>
                {!locked && (node.read
                    ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">✓ Read</span>
                    : node.read_required ? <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Unread</span> : null)}
                {!locked && <ChevronRight className="w-4 h-4 text-stone-400 shrink-0" />}
            </div>
        );
    };

    const FolderCard = ({ node }) => (
        <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft overflow-hidden">
            <div className="px-3 py-2.5 flex items-center gap-2 border-b border-stone-200 bg-stone-100/50">
                <Folder className="w-4 h-4 text-amber-500" />
                <span className="text-[12px] wiki-display font-bold text-stone-900 flex-1">{node.name}</span>
                <span className="text-[10px] text-stone-400 font-mono">{countDocs(node)} pages</span>
            </div>
            <div>
                {(node.children || []).map(c => c.kind === 'folder'
                    ? <div key={c.folder_id} className="pl-3 border-b border-stone-200 last:border-b-0">
                        <div className="flex items-center gap-2 pt-2 text-[11px] font-semibold text-stone-600"><Folder className="w-3.5 h-3.5 text-amber-500" />{c.name}</div>
                        {(c.children || []).map(d => <DocRow key={d.page_id} node={d} />)}
                    </div>
                    : <DocRow key={c.page_id} node={c} />)}
            </div>
        </div>
    );

    return (
        <div className="p-6 max-w-[860px] mx-auto">
            <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${colors.tile}`}>
                    {dept?.icon && <dept.icon className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h1 className="wiki-display font-extrabold text-stone-900 text-[24px] tracking-tight">{dept?.name} Wiki</h1>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-800 border border-stone-300 capitalize">
                            <ShieldCheck className="w-3 h-3" /> {access}</span>
                    </div>
                    <p className="text-[13px] text-stone-500 mt-1 max-w-xl">Everything the {dept?.name.toLowerCase()} team needs in one place — SOPs, playbooks and policies. Ask the agent for an answer, or browse the pages below.</p>
                </div>
                <button onClick={() => onOpenAgent(deptId)}
                    className="wiki-grad text-white h-9 px-3.5 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 wiki-shadow-ai shrink-0">
                    <Sparkles className="w-3.5 h-3.5" /> Ask {dept?.name} Agent
                </button>
            </div>

            {needsRead.length > 0 && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <BellRing className="w-4 h-4 text-amber-600" />
                        <span className="text-[12px] wiki-display font-bold text-amber-800">Needs your acknowledgement</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{needsRead.length}</span>
                    </div>
                    <div className="space-y-1">
                        {needsRead.map(d => (
                            <button key={d.page_id} onClick={() => onSelectDoc(d.page_id)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white text-left">
                                <FileText className="w-3.5 h-3.5 text-amber-600" />
                                <span className="text-[12px] font-medium text-stone-900 flex-1 truncate">{d.name}</span>
                                <span className="text-[11px] font-semibold text-amber-700">Read &amp; confirm →</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {results ? (
                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-2">{results.length} {results.length === 1 ? 'result' : 'results'} for “{filter}”</div>
                    <div className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft overflow-hidden">
                        {results.map(d => <DocRow key={d.page_id} node={d} />)}
                        {results.length === 0 && <div className="text-center text-stone-400 text-[13px] py-10">No pages match.</div>}
                    </div>
                </div>
            ) : (
                <div className="mt-5 space-y-4">
                    {(nodes || []).map(n => n.kind === 'folder'
                        ? <FolderCard key={n.folder_id} node={n} />
                        : <div key={n.page_id} className="bg-white rounded-xl border border-stone-200 wiki-shadow-soft overflow-hidden"><DocRow node={n} /></div>)}
                    {(!nodes || nodes.length === 0) && <div className="text-center text-stone-400 text-[13px] py-12">This department has no pages yet.</div>}
                </div>
            )}
        </div>
    );
}
